import Typography from '@mui/material/Typography';
import ExthrlyTable from './ExthrlyTable';
import { getAcisData, windchill, heatindex, setupFilters, passesFilter } from '../js/dataAccessRoutines';
import { variableName } from '../js/constants';
import { roundFixValue } from '../js/utilityRoutines';

// Extremes processing //

const traceValue = Number.EPSILON; //0.0000001;
const variablePrecision = {
  'temp':   {value: 0, smry: 1},
  'dwpt':   {value: 0, smry: 1},
  'wetb':   {value: 0, smry: 1},
  'wchill': {value: 0, smry: 1},
  'htindx': {value: 0, smry: 1},
  'pcpn':   {value: 2, smry: 2},
  'wspd':   {value: 1, smry: 1},
  'wgust':  {value: 1, smry: 1},
  'rhum':   {value: 0, smry: 1},
  'pres':   {value: 2, smry: 2},
  'slp':    {value: 2, smry: 2},
  'altp':   {value: 2, smry: 2},
  'trange': {value: null, smry: 0},
  'srad':   {value: 1, smry: 1},
}

const setExthrlyResultState = (results, setResult) => {
  if (results) {
      const code = (
        <ExthrlyTable 
			tabledata={results.tabledata}
			sumObs={results.sumObs}
			sumMiss={results.sumMiss}
			userParams={results.userParams}
			stnName={results.stnName}
        />
      );
      setResult(code)
  } else {
    setResult("")
  }
}

const summarizeExthrly = (valueHistogram, userParams) => {
	var valueKeys, tabledata=[];
	if (userParams.smry === 'min') {  
			valueKeys = Object.keys(valueHistogram).sort((a, b) => parseFloat(a)-parseFloat(b))
	} else {
			valueKeys = Object.keys(valueHistogram).sort((a, b) => parseFloat(b)-parseFloat(a))
	}
	let sumrecs = 0;
	var first;
	valueKeys.forEach((val) => {
		first = true;
		if (sumrecs < userParams.numrec) {
			valueHistogram[val].forEach((dt) => {
				sumrecs += 1;
				const value = userParams.variable === "pcpn" && Math.abs(val - traceValue) === 0 ? "Trace" : val;
				tabledata.push({
					index: sumrecs,
					rank: first ? sumrecs : "-",
					val: value, 
					dt: dt
				})
				first = false;
			})
		}
	})
  return tabledata
}

const processData = (res, userParams) => {
	var rval;
	const numhrs = userParams.numhrs;
	const precision = variablePrecision[userParams.variable][numhrs === 1 ? 'value' : 'smry'];
	const stnName = res.meta.name + ", " + res.meta.state
	const filters = setupFilters(userParams)
	const noVal = 'na'  //code for when wind chill or heat index input outside range
	//initializations
	let sumObs = 0
	let sumMiss = 0
	let valueHistogram = {}
	// preprocess - replace values in return with derived values when applicable
	if (['wchill','htindx','pcpn'].includes(userParams.variable)) {
		res.data.forEach((results, i) => {
			// derived variables
			if (userParams.variable === 'wchill') {
				res.data[i][1] = windchill(results[1], results[2], noVal)
			} else if (userParams.variable === 'htindx') {
				res.data[i][1] = heatindex(results[1], results[2], noVal)
			} else if (userParams.variable === 'pcpn' && results[1] === "T") {
				res.data[i][1] = traceValue	//trace value for sorting
			}
		})
	}
	//process
	res.data.forEach((results, i) => {
		// date and hour filter
		if (passesFilter(results[0], filters)) {
			if (numhrs > 1) {
				// get multi-hour values
				const hvs = [];
				for (var j = 0; j < numhrs; j += 1) {
					if (i-j >= 0 && !["M",noVal].includes(res.data[i-j][1])) {
						hvs.push(parseFloat(res.data[i-j][1]));
					}
				}
				if (hvs.length > 0) {
					if (userParams.variable === 'trange') {
						rval = Math.max(...hvs) - Math.min(...hvs)
					} else {
						const rsum = hvs.reduce((accumulator, currentValue) => accumulator + currentValue, 0); // Calculate the sum
						rval = ['pcpn','srad'].includes(userParams.variable) ? rsum : rsum / hvs.length; 
					}
				} else {
					rval = "M";
				} 
			} else {
				rval = ["M",noVal].includes(results[1]) ? "M" : parseFloat(results[1]);
			}
			// now one value per day; filter values for overall summaries
			if (rval !== "M" ) {
				// group date/hour when each value occurred
				if (userParams.variable === "pcpn" && rval > 0.00 && rval < 0.01) { 
					rval = traceValue 
				} else {
					rval = roundFixValue(rval, precision)
				}
				if (!valueHistogram.hasOwnProperty(rval)) {
					valueHistogram[rval] = [];
				}
				valueHistogram[rval].unshift(results[0]);		// add to beginning of array so most recent are first
				sumObs += 1
			} else {
				sumMiss += 1
			}
		}
	})
	const tabledata = summarizeExthrly(valueHistogram, userParams)
	return {tabledata, sumObs, sumMiss, stnName}
}

export function processExthrly(userParams, setResult, hourlyVx) {
	if (['pcpn', 'wspd'].includes(userParams.variable) && userParams.smry === 'min') {
		setResult((
			<Typography sx={{color: "red"}}>
				"Lowest" is not a valid summary type for "{variableName[userParams.variable].name}".
			</Typography>
		))
	} else if (userParams.variable === "trange" && userParams.numhrs <= 1) {
		setResult((
			<Typography sx={{color: "red"}}>
				"Temperature range" requires "Number of hours" to be greater than 1.
			</Typography>
		))
	} else {
		const sid = userParams.sid;
		const network = sid.indexOf(" ") > 0 ? sid.split(" ")[1] : 'icao';
		// some variables need to be calculated from others; get what's needed
		var elems;
		if (userParams.variable === 'wchill') {
			elems = [ {vX: hourlyVx[network]['temp'], interval: [0,0,0,1], duration: 1}, {vX: hourlyVx[network]['wspd'], interval: [0,0,0,1], duration: 1} ]
		} else if (userParams.variable === 'htindx') {
			elems = [ {vX: hourlyVx[network]['temp'], interval: [0,0,0,1], duration: 1}, {vX: hourlyVx[network]['rhum'], interval: [0,0,0,1], duration: 1} ]
		} else if (userParams.variable === 'trange') {
			elems = [ {vX: hourlyVx[network]['temp'], interval: [0,0,0,1], duration: 1} ]
		} else {
			elems = [ {vX: hourlyVx[network][userParams.variable], interval: [0,0,0,1], duration: 1} ]
		}
		// for multi-hour requests, back up so first period has complete set of hours
		const starttime = userParams.fromDate.hour(1).subtract(userParams.numhrs-1, 'hours')
		const sdate = [starttime.year(),starttime.month()+1,starttime.date(),starttime.hour()] || [2023,12,1,1];
		// end hour 24, which is hour 0 the next day
		const nextday = new Date(userParams.toDate);
		nextday.setDate(userParams.toDate.date() + 1)
		const edate = [nextday.getFullYear(),nextday.getMonth()+1,nextday.getDate(),0] || [2024,1,1,0];

		const acisParams = {
				sid: sid,
				sDate: sdate,
				eDate: edate,
				elems: elems,
		}
		getAcisData(acisParams)
		.then(res => {
			const results = processData(res, userParams);
			setExthrlyResultState({...results, userParams}, setResult)
		})
		.catch(error =>  { 
			console.log("Data retrieval error: " + error); 
			setResult((
				<Typography sx={{color: "red"}}>
					Error processing request
				</Typography>
			))
		})
	}

}